<script lang="ts" setup>
  import { useControllersStore } from '@/stores/controllers';
  import { useGroupsStore } from '@/stores/groups';
  import { useOrganizationsStore } from '@/stores/organizations';
  import { flattenGroup } from '@/utils/flattenGroup';
  import { type Breadcrumb, BreadcrumbItem, type BreadcrumbsProps } from './types';

  const props = withDefaults(defineProps<BreadcrumbsProps>(), {
    items: () => [],
  });

  const { t } = useI18n({
    messages: {
      ru: {
        organizationsBreadcrumb: 'Организации',
        controllersBreadcrumb: '{organizationName}',
      },
      en: {
        organizationsBreadcrumb: 'Organizations',
        controllersBreadcrumb: '{organizationName}',
      },
    },
  });

  const groupsStore = useGroupsStore();
  const controllersStore = useControllersStore();
  const organizationsStore = useOrganizationsStore();

  // #FIXME This is not a good way to do this, but i haven't way to properly do this because of issues with i18n. At least it works and DRY:)
  const breadcrumbs = computed(() => {
    const controller = controllersStore.controller;
    const organization = organizationsStore.organization;

    const items = props.items
      .map((item) => {
        if (item === BreadcrumbItem.ORGANIZATIONS) {
          return {
            label: t('organizationsBreadcrumb'),
            to: { name: 'organizations' },
          };
        } else if (item === BreadcrumbItem.ORGANIZATION && organization) {
          return {
            label: organization.name,
            to: {
              name: 'controllers',
              params: {
                organizationId: organization.id,
              },
            },
          };
        } else if (item === BreadcrumbItem.CONTROLLERS && organization) {
          return {
            label: t('controllersBreadcrumb', {
              organizationName: organization.name,
            }),
            to: {
              name: 'controllers',
              params: {
                organizationId: organization.id,
              },
            },
          };
        } else if (
          item === BreadcrumbItem.CONTROLLER &&
          controller &&
          organization
        ) {
          return {
            label: controller.serialNumber,
            to: {
              name: 'controller',
              params: {
                organizationId: organization.id,
                serialNumber: controller.serialNumber,
              },
            },
          };
        }

        return undefined;
      })
      .filter((item) => item !== undefined) as Breadcrumb[];

    // @ts-ignore
    if (items.find((item) => item.to?.name === 'controller')) {
      const rootGroup = controller?.group
        ? groupsStore.getGroupPathById(controller.group.id)
        : null;

      if (rootGroup) {
        const groups = flattenGroup(rootGroup).map((group) => ({
          label: group.name,
          to: {
            name: 'controllers',
            params: { organizationId: organization?.id },
            query: { groupIds: group.id },
          },
        }));

        items.splice(items.length - 1, 0, ...groups);
      }
    }

    return items;
  });
</script>

<template>
  <nav class="text-sm">
    <ul class="flex gap-4 flex-wrap">
      <template v-for="(breadcrumb, index) in breadcrumbs" :key="index">
        <li class="flex gap-2">
          <RouterLink class="link whitespace-nowrap" :to="breadcrumb.to">
            {{ breadcrumb.label }}</RouterLink
          >
        </li>
        <span v-if="index < breadcrumbs.length - 1">/</span>
      </template>
    </ul>
  </nav>
</template>

<style>
  .router-link-active.link {
    @apply text-black no-underline cursor-auto;
  }
</style>
